import { useState } from 'react';
import { cx } from '@/components/ui';
import { HealthPanel } from './monitoring/HealthPanel';
import { MembersPanel } from './MembersPanel';
import { SessionsPanel } from './SessionsPanel';
import { AiUsagePanel } from './AiUsagePanel';
import { BatchPanel } from './BatchPanel';
import { ErrorLogPanel } from './ErrorLogPanel';
import { PaperResetPanel } from '@/features/trading/PaperResetPanel';
import { AiReviewSwitchCard } from '@/features/trading/AiReviewSwitchCard';
import { MyInfoPanel } from '@/features/auth/MyInfoPanel';

/**
 * 관리자 페이지.
 *
 * 범위는 00-architecture.md 1절에서 정한 넷이다.
 *   모니터링       Phase 0 의 헬스체크 화면을 여기로 옮겼다
 *   사용자·권한     계정 생성과 메뉴 권한 매트릭스. 가입 페이지가 없으므로 유일한 생성 경로다
 *   활성 세션       기기 분실 시 유일한 대응 수단
 *   AI 비용         유일하게 돈이 나가는 외부 호출을 사람이 확인하는 자리
 *
 * 모니터링이 왜 여기로 왔나
 *   Phase 0 에서는 루트 화면이 곧 헬스체크 화면이었다. 전 구간 연결을 확인하는 것이
 *   그때의 목표였기 때문이다. 이제는 가족이 함께 쓰는 대시보드라
 *   CPU 와 메모리는 관리자만 볼 정보다.
 *
 * 라우터 대신 상태로 탭을 가르는 이유
 *   탭 넷이 전부 관리자 전용이라 URL 로 구분해 얻을 것이 없다.
 *   중첩 라우트를 두면 App.tsx(공유 영역)를 탭이 늘 때마다 고쳐야 한다.
 */

/*
  '내 정보' 가 마지막에 있는 이유 (2026-08-17, 3.6)

  일반 회원은 상단에 '내 정보' 탭을 받지만 관리자는 안 받는다.
  받으면 상단 탭이 6개가 되어 폰에서 가로 스크롤이 길어진다.
  대신 여기에 넣어 관리자도 자기 표시 이름·비밀번호·글꼴을 바꿀 수 있게 한다.

  화면은 일반 회원 것과 같은 컴포넌트다. (features/auth/MyInfoPanel)
  둘로 두면 한쪽에만 항목을 추가하는 일이 반드시 생긴다.

  앞이 아니라 뒤에 두는 것은 이 탭이 관리 업무가 아니기 때문이다.
  관리자 화면을 여는 이유는 대개 사용자·배치를 보려는 것이다.
*/
const TABS = [
  { key: 'members', label: '사용자·권한' },
  { key: 'sessions', label: '세션' },
  { key: 'ai', label: 'AI 비용' },
  { key: 'batch', label: '배치' },
  { key: 'paper', label: '모의투자' },
  { key: 'monitoring', label: '모니터링' },
  { key: 'errors', label: '오류' },
  { key: 'me', label: '내 정보' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function AdminPage() {
  const [tab, setTab] = useState<TabKey>('members');

  return (
    <div className="flex flex-col gap-4">
      <nav className="-mx-4 overflow-x-auto px-4">
        <div className="flex gap-1 border-b border-line pb-2">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={cx(
                'min-h-[36px] whitespace-nowrap rounded-lg px-3 text-sm',
                tab === item.key ? 'bg-brand text-brand-fg' : 'text-fg-muted hover:bg-surface-hover',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {tab === 'members' && <MembersPanel />}
      {tab === 'sessions' && <SessionsPanel />}
      {/* AI 2차 검토 호출 켜고 끄기는 돈이 나가는 스위치라 비용 표 위에 둔다(2026-10-19 · V158) */}
      {tab === 'ai' && (
        <>
          <AiReviewSwitchCard />
          <AiUsagePanel />
        </>
      )}
      {tab === 'batch' && <BatchPanel />}
      {tab === 'paper' && <PaperResetPanel />}
      {tab === 'monitoring' && <HealthPanel />}
      {tab === 'errors' && <ErrorLogPanel />}
      {tab === 'me' && <MyInfoPanel />}
    </div>
  );
}
